import Component from "../../lib/component";
import Tracker from "../../lib/tracker";
import {PaneSlidesConfig} from "../pane_slides/pane_slides";

export const HomeHeroConfig = {
    componentTypeKey: 'HomeHero',
    baseHtmlClass: 'home_hero',
    selector: '.home_hero',

    datasetConfigKey: 'home_hero__config',

    closeSelector: '.home_hero__close',
    continueSelector: '.home_hero__continue',

    // Set on <html>, matching the inline script that suppresses the deck for a returning visitor —
    // so one CSS rule covers both the returning-visitor and the dismissed-mid-visit case.
    seenHtmlClass: 'home--hero_seen',

    // Held on <html> only while the deck still occupies the viewport, which is what upgrades the
    // root scroller from `proximity` to `mandatory` — see "TIGHTENING THE PULL" in the stylesheet.
    // It MUST come off before the content below is reachable, or the page below the hero cannot be
    // scrolled to at all.
    snappingHtmlClass: 'home--hero_snapping',

    // Reported with BUTTON_SELECTED, mirroring the app's onboarding Continue so the two decks'
    // completions are comparable.
    continueButtonId: 'HOME_HERO__CONTINUE',

    // The collapse. `dismissingState` lifts the deck out of flow and `collapsedState` drives its
    // height to zero — see "THE DISMISSAL COLLAPSE" in `home/_hero_carousel.scss`.
    dismissingState: 'dismissing',
    collapsedState: 'collapsed',
    freezeOffsetProperty: '--home_hero--freeze_offset',

    // Must exceed the CSS height transition. A `transitionend` that never arrives — an interrupted
    // transition, a zero-length collapse, a browser that skips it — would otherwise leave the deck
    // fixed over the page forever, so the timer is the thing that guarantees completion, not a
    // nicety. Deliberately generous: finishing late is invisible, finishing early is a jump cut.
    collapseTimeoutMs: 700,

    // How far past the hero's bottom edge still counts as "scrolled past", in CSS pixels.
    //
    // NOT slack for its own sake. The traveller's natural resting place after the deck is the
    // content-start snap position, where the hero's bottom edge sits ON the viewport's top edge —
    // and it lands there a fraction OFF, because scroll offsets quantise to whole device pixels
    // while the slide height (a `vh` value) generally does not. Measured 2026-08-07: the hero's
    // `bottom` came to rest at 0.355 rather than 0, so a strict `> 0` test never fired and the
    // deck was never marked as seen. See `doc/README--scroll_snapping.md` → "Scroll offsets
    // quantise to whole DEVICE pixels".
    scrolledPastTolerancePx: 2,
};

// Records that the traveller has seen the home page hero deck, so a returning visit skips it.
//
// THIS COMPONENT ONLY WRITES THE FLAG — it never hides anything. The suppression half lives in an
// inline script at the top of `app/views/home/_hero_carousel.html.erb`, which adds a class to
// <html> before the hero element is even parsed. That has to happen far earlier than any bundle
// can run, or the deck paints and then vanishes.
//
// It must also be client-side on both halves: the home page is page-cached
// (`caches_page_with_host :index`), so every visitor is served byte-identical HTML and the server
// cannot know who has seen the deck.
export class HomeHero extends Component {

    static get config() {
        return HomeHeroConfig;
    }

    constructor(id_or_element) {
        super({
            element: id_or_element,
            config: HomeHero.config
        });

        this.setDatasetConfig(HomeHeroConfig.datasetConfigKey);
        this.seenStorageKey = this.getDatasetConfigValue('seen_storage_key');
        // Both names are the server's to choose — see HomeHelper::HERO_PASSED_FLAG / _EVENT.
        this.passedFlag = this.getDatasetConfigValue('passed_flag');
        this.passedEvent = this.getDatasetConfigValue('passed_event');

        if (!this.seenStorageKey) {
            console.error('[HomeHero] No `seen_storage_key` in the dataset config — the deck will show on every visit.');
            return;
        }

        // Already flagged (so this visitor is seeing a `display: none` hero, or has scrolled past
        // on an earlier visit) — nothing to watch for and no controls to wire.
        if (readSeenFlag(this.seenStorageKey)) return;

        this.tracker = new Tracker(window, document, {});
        this.hasDismissed = false;

        // Whether the traveller reached the end before leaving, which is the only thing that
        // distinguishes a completion from a bail. `SLIDES_COMPLETED` is emitted by the carousel
        // itself and bubbles up to this element.
        this.hasCompleted = false;
        this.element.addEventListener(PaneSlidesConfig.completedEventName, () => {
            this.hasCompleted = true;
        });

        this.listenToControls();

        // A `window` listener OUTLIVES this element, so it has to be registered for teardown —
        // otherwise the component and its whole subtree are never collected.
        this.hasPassed = false;
        this.handleScroll = this.handleScroll.bind(this);
        window.addEventListener('scroll', this.handleScroll, {passive: true});
        this.onDestroy(() => {
            window.removeEventListener('scroll', this.handleScroll);
            document.documentElement.classList.remove(HomeHeroConfig.snappingHtmlClass);
        });

        // No scroll event fires at load, so the snapping class would not be applied until the
        // traveller first moved — the one moment they are most likely to land between slides.
        this.handleScroll();
    }

    // Runs on every scroll, and does two jobs off one measurement.
    //
    // It DELIBERATELY does not remove itself once the deck is passed, though the seen-flag half is
    // one-shot: the snapping class has to keep tracking, because scrolling back UP into the deck
    // must re-engage `mandatory` and scrolling down out of it must release the page.
    //
    // Marked seen once the hero has been scrolled fully past — not on load, and not when the
    // carousel reports completion. Scrolling past is the unambiguous signal that the traveller is
    // done with the deck however they got there: reading every slide, skipping quickly, or landing
    // on a restored scroll position. Someone who arrives and leaves without scrolling is not
    // marked and sees the deck again next time, which is intended.
    handleScroll() {
        if (this.isDestroyed) return;

        // Measured live rather than from a cached height, so a resize or a change of slide height
        // cannot leave this stale. The tolerance is load-bearing — the deck comes to rest a
        // fraction short of zero because scroll offsets quantise to whole device pixels; see the
        // config, and `doc/README--scroll_snapping.md`.
        const hasPassedDeck =
            this.element.getBoundingClientRect().bottom <= HomeHeroConfig.scrolledPastTolerancePx;

        // Off BEFORE the content below is reachable — the ordering the whole approach rests on.
        document.documentElement.classList.toggle(HomeHeroConfig.snappingHtmlClass, !hasPassedDeck);

        if (!hasPassedDeck || this.hasPassed) return;
        this.hasPassed = true;

        writeSeenFlag(this.seenStorageKey);
        this.announcePassed();
    }

    // Own-element listeners need no teardown registration — the library unwires those itself.
    listenToControls() {
        const closeElement = this.element.querySelector(HomeHeroConfig.closeSelector);
        if (closeElement) closeElement.addEventListener('click', () => this.handleDismiss());

        const continueElement = this.element.querySelector(HomeHeroConfig.continueSelector);
        if (continueElement) continueElement.addEventListener('click', () => this.handleContinue());
    }

    // The close cross. Reaching the last slide and then closing is a completion, not a bail — so
    // only report a dismissal when the traveller left before the end. Mirrors the app's onboarding.
    handleDismiss() {
        if (!this.hasCompleted) {
            this.trackEvent('SLIDES_DISMISSED', HomeHeroConfig.componentTypeKey);
        }
        this.dismiss();
    }

    handleContinue() {
        this.trackEvent('BUTTON_SELECTED', HomeHeroConfig.continueButtonId, {
            button_id: HomeHeroConfig.continueButtonId,
        });
        this.dismiss();
    }

    // Takes the deck out of the page and puts the traveller at the top of the content.
    //
    // A PRESS IS THE SETTLED MOMENT, which is what keeps this simple. Removing four viewports of
    // content from above the scroll position normally demands compensating for the reflow — but
    // the deck is everything above the content, so once it is gone the content starts at zero and
    // the correct scroll position is simply the top. No measurement, no arithmetic, and no
    // dependence on scroll anchoring (which Chrome and Firefox implement and Safari does not).
    dismiss() {
        if (this.hasDismissed) return;
        this.hasDismissed = true;

        writeSeenFlag(this.seenStorageKey);
        this.announcePassed();

        // The scroll watcher has nothing left to watch — and it must stop BEFORE the collapse
        // scrolls the page, or it would re-apply the snapping class on the way.
        window.removeEventListener('scroll', this.handleScroll);
        document.documentElement.classList.remove(HomeHeroConfig.snappingHtmlClass);

        if (prefersReducedMotion()) {
            this.completeDismissal();
            return;
        }
        this.collapse();
    }

    // Rolls the deck up rather than cutting to the page. See "THE DISMISSAL COLLAPSE" in the
    // stylesheet for why it goes via `position: fixed` instead of animating the flow height.
    collapse() {
        // MEASURED BEFORE THE ELEMENT GOES FIXED — afterwards its `top` is 0 by definition and the
        // freeze would show slide 1 rather than the slide pressed from. Negative once scrolled in.
        const offsetFromViewportTop = Math.round(this.element.getBoundingClientRect().top);
        this.element.style.setProperty(HomeHeroConfig.freezeOffsetProperty, `${offsetFromViewportTop}px`);

        this.addHtmlClassState(HomeHeroConfig.dismissingState);

        // The deck is out of flow now, so the document is four viewports shorter and the content
        // sits at the top — behind the fixed deck, which is what makes the reveal seamless.
        window.scrollTo(0, 0);

        // Flush the fixed layout so the browser has a height to transition FROM. Without this the
        // two class changes coalesce into one style resolution and there is no transition at all.
        void this.element.offsetHeight;
        this.addHtmlClassState(HomeHeroConfig.collapsedState);

        this.element.addEventListener('transitionend', (event) => {
            if (event.propertyName === 'height') this.completeDismissal();
        });

        this.collapseTimer = setTimeout(() => this.completeDismissal(), HomeHeroConfig.collapseTimeoutMs);
        this.onDestroy(() => clearTimeout(this.collapseTimer));
    }

    // The finished state, reached either by the collapse or straight away. Idempotent, because
    // `transitionend` and the timeout race and both are meant to be able to win.
    completeDismissal() {
        if (this.hasCompletedDismissal) return;
        this.hasCompletedDismissal = true;

        clearTimeout(this.collapseTimer);

        // The same class the inline script sets for a returning visitor, so one CSS rule ends both
        // journeys — and the deck is `display: none` from here, not merely a zero-height fixed box.
        document.documentElement.classList.add(HomeHeroConfig.seenHtmlClass);
        this.removeHtmlClassState(HomeHeroConfig.dismissingState);
        this.removeHtmlClassState(HomeHeroConfig.collapsedState);
        this.element.style.removeProperty(HomeHeroConfig.freezeOffsetProperty);

        // Belt and braces: the reduced-motion path never went fixed, so nothing has scrolled yet.
        window.scrollTo(0, 0);
    }

    // `eventLabel` is included for backwards compatibility with older native apps — see
    // `pane_slides.js#trackEvent`, which this mirrors.
    trackEvent(eventNameKey, eventLabel, parameters) {
        if (!window.RankersAnalytics || !window.RankersAnalytics.EventName) return;
        const eventName = window.RankersAnalytics.EventName[eventNameKey];
        if (!eventName || !this.tracker) return;
        this.tracker.eventTracker(eventName, {eventLabel: eventLabel, ...parameters});
    }

    // Tells the rest of the page that the traveller has reached the content, for anything that
    // deliberately holds off until then rather than competing with the deck for attention (the
    // deals widget). Raise the flag BEFORE firing the event, so a listener that inspects the flag
    // rather than trusting the event sees a consistent world either way.
    //
    // The flag alone is not enough and the event alone is not enough — see the note on
    // HomeHelper::HERO_PASSED_FLAG for why both exist.
    announcePassed() {
        if (!this.passedFlag || !this.passedEvent) return;
        window[this.passedFlag] = true;
        document.dispatchEvent(new CustomEvent(this.passedEvent));
    }

}

// Guarded because `matchMedia` is absent in some embedded webviews; treating an unanswerable
// question as "no preference" keeps the animation for everyone the query can't speak for.
function prefersReducedMotion() {
    try {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (error) {
        return false;
    }
}

// localStorage access THROWS in Safari private browsing and wherever storage is blocked, so every
// access is guarded. Failing closed means the deck simply keeps showing, which is the safe outcome.
function readSeenFlag(storageKey) {
    try {
        return window.localStorage && window.localStorage.getItem(storageKey);
    } catch (error) {
        return null;
    }
}

function writeSeenFlag(storageKey) {
    try {
        window.localStorage.setItem(storageKey, '1');
    } catch (error) {}
}
